import { Icon } from "@mdi/react";
import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  mdiCalendarClock,
  mdiCodeTags,
  mdiDelete,
  mdiFormatBold,
  mdiFormatHeader1,
  mdiFormatHeader2,
  mdiFormatItalic,
  mdiFormatListBulleted,
  mdiFormatListNumbered,
  mdiFormatQuoteClose,
  mdiImageOutline,
  mdiLink,
  mdiTable
} from "@mdi/js";
import {
  Button,
  DatePicker,
  Divider,
  Input,
  Modal,
  Radio,
  Select,
  Tabs,
  TimePicker
} from "antd";
import { utc } from "dayjs";
import { marked } from "marked";
import moment from "moment";
import styled from "styled-components";
import { arrayOrEmpty, uid } from "utils";

import { IconButton } from "components/forecasting/Shared";

import useCreateAutomationSchedule from "../hooks/useCreateAutomationSchedule";
import useDeleteAutomationSchedule from "../hooks/useDeleteAutomationSchedule";
import useFetchAutomationSchedules from "../hooks/useFetchAutomationSchedules";
import useUpdateAutomationSchedule from "../hooks/useUpdateAutomationSchedule";
import { AutomationCollection } from "../types/automation";
import { Topics } from "../types/topics";

const { Option } = Select;
const { TextArea } = Input;
interface ScheduleRule {
  _id?: string;
  id?: string;
  name?: string;
  automationId: string;
  schedule: {
    type: "once" | "daily" | "weekly" | "monthly";
    startDate: string;
    endDate?: string;
    time?: string;
    dayOfWeek?: number;
    dayOfMonth?: number;
  };
  emailTemplate?: {
    subject: string;
    body: string;
    recipients: string[];
  };

  datetime?: moment.Moment; // For UI convenience
}

interface AutomationScheduleModalProps {
  open: boolean;
  automation: AutomationCollection | null;
  onCancel: () => void;
  onSave?: (automation: AutomationCollection, schedules: ScheduleRule[]) => void;
}

const ModalContent = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const ScheduleItem = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fafafa;
`;

const ScheduleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;

const FrequencyOptions = styled.div`
  margin-bottom: 12px;
`;

const DaysOfWeekSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const DayButton = styled(Button)<{ selected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => (props.selected ? "#1890ff" : "white")};
  color: ${(props) => (props.selected ? "white" : "black")};
  border: 1px solid ${(props) => (props.selected ? "#1890ff" : "#d9d9d9")};

  &:hover {
    background: ${(props) => (props.selected ? "#40a9ff" : "#f5f5f5")};
    border-color: ${(props) => (props.selected ? "#40a9ff" : "#d9d9d9")};
  }
`;

const EmailTemplateSection = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 10px;
  background: #f9f9f9;
`;

const EmailTemplateContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const EmailContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;
const ScreenshotPreview = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  width: 300px;
`;

const ScreenshotList = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const ScreenshotItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #e6e6e6;
  }

  &:active {
    background: #d9d9d9;
  }
`;

const EmailPreview = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  min-height: 100px;
  white-space: pre-wrap;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
`;

const MarkdownToolbar = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  background: #fafafa;
  flex-wrap: wrap;
`;

const MarkdownButton = styled(Button)`
  min-width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e6f7ff;
    border-color: #91d5ff;
  }
`;

const MarkdownTextArea = styled(TextArea)`
  border-radius: 0 0 4px 4px !important;
  border-top: none !important;
`;

const CompactTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 8px !important;
  }

  .ant-tabs-tab {
    padding: 4px 12px !important;
    margin: 0 2px !important;
  }

  .ant-tabs-content-holder {
    padding-top: 0 !important;
  }

  .ant-tabs-tabpane {
    padding: 0 !important;
    height: calc(100% - 20px);
  }

  .ant-tabs-content {
    height: calc(100% - 20px);
  }
`;

export const AutomationScheduleModal: FC<AutomationScheduleModalProps> = ({
  open,
  automation,
  onCancel,
  onSave
}) => {
  const [schedules, setSchedules] = useState<ScheduleRule[]>([]);
  const [emailTemplate, setEmailTemplate] = useState({
    subject: "Automation Report: {{automation_name}} - {{date}}",
    body: `# Automation Report

**Automation:** {{automation_name}}
**Date:** {{date}}

## Generated Screenshots


`,
    recipients: []
  });

  const { data: existingSchedules } = useFetchAutomationSchedules();
  const { mutateAsync: createSchedule, isLoading: isCreating } =
    useCreateAutomationSchedule();
  const { mutateAsync: updateSchedule, isLoading: isUpdating } =
    useUpdateAutomationSchedule();
  const { mutateAsync: deleteSchedule } = useDeleteAutomationSchedule();

  useEffect(() => {
    if (open && automation) {
      // Filter existing schedules for this automation
      const automationSchedules =
        existingSchedules?.filter(
          (schedule) => schedule.automationId === automation._id
        ) || [];

      // Convert backend schedules to UI format
      const uiSchedules = automationSchedules.map(backendScheduleToUI);

      setSchedules(uiSchedules);

      // Set email template from first schedule if available
      if (uiSchedules.length > 0 && uiSchedules[0].emailTemplate) {
        setEmailTemplate(uiSchedules[0].emailTemplate);
      }
    }
  }, [open, automation, existingSchedules]);

  const createNewSchedule = (): ScheduleRule => ({
    id: uid(),
    name: uid(),
    automationId: automation?._id || "",
    schedule: {
      type: "once",
      startDate: moment().add(1, "hour").startOf("hour").toISOString(),
      time: moment().add(1, "hour").startOf("hour").format("HH:mm"),
      dayOfWeek: 1, // Monday
      dayOfMonth: 1
    },

    datetime: moment().add(1, "hour").startOf("hour")
  });

  const addNewSchedule = () => {
    const newSchedule = createNewSchedule();
    setSchedules([newSchedule]);
  };

  const getNextExecutionTime = (schedule: ScheduleRule): string => {
    if (!schedule.datetime) return "Not set";

    const now = moment();
    const scheduleTime = schedule.datetime;

    switch (schedule.schedule.type) {
      case "once":
        return scheduleTime.isBefore(now)
          ? "Already executed"
          : scheduleTime.format("MMMM Do YYYY, h:mm a");

      case "daily": {
        const nextDaily = now
          .clone()
          .hour(scheduleTime.hour())
          .minute(scheduleTime.minute())
          .second(0)
          .millisecond(0);

        // If today's time has passed, move to tomorrow
        if (nextDaily.isSameOrBefore(now)) {
          nextDaily.add(1, "day");
        }
        return nextDaily.format("MMMM Do YYYY, h:mm a");
      }

      case "weekly": {
        if (schedule.schedule.dayOfWeek === undefined) return "Day not set";

        const nextWeekly = now
          .clone()
          .day(schedule.schedule.dayOfWeek)
          .hour(scheduleTime.hour())
          .minute(scheduleTime.minute())
          .second(0)
          .millisecond(0);

        // If this week's scheduled time has passed, move to next week
        if (nextWeekly.isSameOrBefore(now)) {
          nextWeekly.add(1, "week");
        }
        return nextWeekly.format("MMMM Do YYYY, h:mm a");
      }

      case "monthly": {
        if (schedule.schedule.dayOfMonth === undefined) return "Day not set";

        let nextMonthly = now
          .clone()
          .date(schedule.schedule.dayOfMonth)
          .hour(scheduleTime.hour())
          .minute(scheduleTime.minute())
          .second(0)
          .millisecond(0);

        // Handle end of month edge cases
        const lastDayOfMonth = now.clone().endOf("month").date();
        if (schedule.schedule.dayOfMonth > lastDayOfMonth) {
          nextMonthly = nextMonthly.endOf("month");
        }

        // If this month's scheduled time has passed, move to next month
        if (nextMonthly.isSameOrBefore(now)) {
          nextMonthly.add(1, "month");
          const nextMonthLastDay = nextMonthly.clone().endOf("month").date();
          if (schedule.schedule.dayOfMonth > nextMonthLastDay) {
            nextMonthly = nextMonthly.endOf("month");
          } else {
            nextMonthly.date(schedule.schedule.dayOfMonth);
          }
        }
        return nextMonthly.format("MMMM Do YYYY, h:mm a");
      }

      default:
        return "Unknown schedule type";
    }
  };

  const backendScheduleToUI = (backendSchedule): ScheduleRule => {
    try {
      // Create datetime from startDate (which is stored in UTC on the backend)
      let datetime = utc(backendSchedule.schedule.startDate);

      // If we have a separate time field (which is stored in UTC), use it to set the time
      // This is important for recurring schedules where date and time are stored separately
      if (backendSchedule.schedule.time) {
        const [hours, minutes] = backendSchedule.schedule.time.split(":").map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          datetime = datetime.hour(hours).minute(minutes).second(0);
        }
      }

      // Convert from UTC to local time for UI display
      return {
        ...backendSchedule,
        datetime: datetime.local()
      };
    } catch (error) {
      // Fallback to current time if conversion fails
      return {
        ...backendSchedule,
        datetime: moment()
      };
    }
  };

  const uiScheduleToBackend = (uiSchedule: ScheduleRule) => ({
    name: uiSchedule.name,
    automationId: uiSchedule.automationId,
    schedule: {
      type: uiSchedule.schedule.type,
      // Convert local datetime to UTC ISO string for backend storage
      startDate: uiSchedule.datetime?.toISOString() || uiSchedule.schedule.startDate,
      // Convert local time to UTC time for cron job scheduling
      time: uiSchedule.datetime?.utc().format("HH:mm") || uiSchedule.schedule.time,
      dayOfWeek: uiSchedule.schedule.dayOfWeek,
      dayOfMonth: uiSchedule.schedule.dayOfMonth
    },
    isActive: true,
    emailTemplate: emailTemplate
  });

  const removeSchedule = async (index: number) => {
    const scheduleToRemove = schedules[index];

    // If schedule exists in backend, delete it
    if (scheduleToRemove._id) {
      try {
        await deleteSchedule(scheduleToRemove._id);
        toast.success("Schedule deleted successfully");
      } catch (error) {
        toast.error("Failed to delete schedule");
        return;
      }
    }

    setSchedules([]);
  };

  const updateScheduleLocal = (index: number, updates: Partial<ScheduleRule>) => {
    const newSchedules = [...schedules];
    newSchedules[index] = { ...newSchedules[index], ...updates };
    setSchedules(newSchedules);
  };

  const toggleDayOfWeek = (scheduleIndex: number, day: number) => {
    const schedule = schedules[scheduleIndex];
    const currentDay = schedule.schedule.dayOfWeek;

    updateScheduleLocal(scheduleIndex, {
      schedule: {
        ...schedule.schedule,
        dayOfWeek: currentDay === day ? undefined : day
      }
    });
  };

  const handleSave = async () => {
    const validSchedules = schedules.filter(
      (schedule) => schedule.datetime && automation
    );

    if (validSchedules.length === 0) {
      toast.error("Please add at least one valid schedule");
      return;
    }

    try {
      for (const schedule of validSchedules) {
        const backendData = uiScheduleToBackend(schedule);

        if (schedule._id) {
          // Update existing schedule
          await updateSchedule({ id: schedule._id, ...backendData });
        } else {
          // Create new schedule
          await createSchedule(backendData);
        }
      }

      toast.success(`Schedule saved for ${automation.name}`);
      onCancel(); // Close modal

      // Call onSave if provided for backward compatibility
      if (onSave) {
        onSave(automation, validSchedules);
      }
    } catch (error) {
      const message = error?.data?.error ?? "Failed to save schedules";
      toast.error(message);
    }
  };

  const getDayLabel = (day: number): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return days[day];
  };

  const getScreenshotCommands = () => {
    if (!automation) return [];

    const screenshotCommands = [];
    arrayOrEmpty(automation.groups).forEach((group) => {
      arrayOrEmpty(group.commands).forEach((command) => {
        if (command.topic === Topics.TakeChartScreenshot) {
          screenshotCommands.push({
            type: "Chart Screenshot",
            title: (command.payload as { title?: string })?.title || "Chart"
          });
        } else if (command.topic === Topics.TakeMapScreenshot) {
          screenshotCommands.push({ type: "Map Screenshot", title: "Map View" });
        }
      });
    });

    return screenshotCommands;
  };

  const renderEmailPreview = () => {
    const screenshots = getScreenshotCommands();
    const screenshotList = screenshots.map((s) => `- ${s.title} (${s.type})`).join("\n");

    return emailTemplate.body
      .replace(/\{\{automation_name\}\}/g, automation?.name || "Automation")
      .replace(/\{\{date\}\}/g, moment().format("MMMM Do YYYY, h:mm a"))
      .replace(
        /\{\{screenshots\}\}/g,
        screenshotList || "No screenshots will be generated"
      );
  };

  const processScreenshotPlaceholders = (htmlContent: string) => {
    const screenshots = getScreenshotCommands();
    let processedContent = htmlContent;

    screenshots.forEach((screenshot, index) => {
      const placeholderRegex = new RegExp(`\\{\\{screenshot_${index + 1}\\}\\}`, "g");
      const placeholderImage = `<span style="display: inline-block; width: 400px; height: 250px; border: 2px dashed #d9d9d9; background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%); margin: 0 8px; text-align: center; border-radius: 8px; position: relative; box-shadow: 0 2px 4px rgba(0,0,0,0.1); vertical-align: top;"><span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #666; font-size: 16px; font-weight: 500;"><span style="width: 40px; height: 32px; border: 2px solid #999; border-radius: 4px; margin: 0 auto 8px; position: relative; display: inline-block;"><span style="width: 8px; height: 8px; background: #999; border-radius: 50%; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); display: block;"></span><span style="width: 12px; height: 4px; background: #999; border-radius: 2px; position: absolute; top: -6px; left: 50%; transform: translateX(-50%); display: block;"></span></span><span style="display: block;">Screenshot ${
        index + 1
      }</span><span style="font-size: 12px; color: #999; margin-top: 4px; display: block;">${
        screenshot.type
      }</span></span></span>`;
      processedContent = processedContent.replace(placeholderRegex, placeholderImage);
    });

    return processedContent;
  };

  const insertMarkdown = (
    prefix: string,
    suffix: string = "",
    placeholder: string = "text"
  ) => {
    const textarea = document.getElementById(
      "email-body-textarea"
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const textToInsert = selectedText || placeholder;
    const newText = prefix + textToInsert + suffix;

    const newValue =
      textarea.value.substring(0, start) + newText + textarea.value.substring(end);

    setEmailTemplate({ ...emailTemplate, body: newValue });

    setTimeout(() => {
      if (selectedText) {
        textarea.setSelectionRange(start, start + newText.length);
      } else {
        textarea.setSelectionRange(
          start + prefix.length,
          start + prefix.length + textToInsert.length
        );
      }
      textarea.focus();
    }, 0);
  };

  const insertScreenshotPlaceholder = (index: number) => {
    const textarea = document.getElementById(
      "email-body-textarea"
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const placeholder = `{{screenshot_${index}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      textarea.value.substring(0, start) + placeholder + textarea.value.substring(end);

    setEmailTemplate({ ...emailTemplate, body: newValue });

    setTimeout(() => {
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      textarea.focus();
    }, 0);
  };

  const insertAllScreenshotsPlaceholder = () => {
    const textarea = document.getElementById(
      "email-body-textarea"
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const screenshots = getScreenshotCommands();
    const placeholders = screenshots
      .map((_, index) => `{{screenshot_${index + 1}}}`)
      .join("\n");
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      textarea.value.substring(0, start) + placeholders + textarea.value.substring(end);

    setEmailTemplate({ ...emailTemplate, body: newValue });

    setTimeout(() => {
      textarea.setSelectionRange(
        start + placeholders.length,
        start + placeholders.length
      );
      textarea.focus();
    }, 0);
  };

  const markdownButtons = [
    {
      icon: mdiFormatBold,
      tooltip: "Bold",
      action: () => insertMarkdown("**", "**", "bold text")
    },
    {
      icon: mdiFormatItalic,
      tooltip: "Italic",
      action: () => insertMarkdown("*", "*", "italic text")
    },
    {
      icon: mdiFormatHeader1,
      tooltip: "Heading 1",
      action: () => insertMarkdown("# ", "", "Heading")
    },
    {
      icon: mdiFormatHeader2,
      tooltip: "Heading 2",
      action: () => insertMarkdown("## ", "", "Heading")
    },
    {
      icon: mdiFormatListBulleted,
      tooltip: "Bulleted List",
      action: () => insertMarkdown("- ", "", "List item")
    },
    {
      icon: mdiFormatListNumbered,
      tooltip: "Numbered List",
      action: () => insertMarkdown("1. ", "", "List item")
    },
    {
      icon: mdiFormatQuoteClose,
      tooltip: "Quote",
      action: () => insertMarkdown("> ", "", "Quote")
    },
    {
      icon: mdiCodeTags,
      tooltip: "Code",
      action: () => insertMarkdown("`", "`", "code")
    },
    {
      icon: mdiLink,
      tooltip: "Link",
      action: () => insertMarkdown("[", "](url)", "link text")
    },
    {
      icon: mdiTable,
      tooltip: "Table",
      action: () =>
        insertMarkdown(
          "| Column 1 | Column 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |",
          "",
          ""
        )
    }
  ];
  const emailTabItems = [
    {
      key: "edit",
      label: "Edit",
      children: (
        <EmailTemplateContent>
          <ScreenshotPreview>
            <strong>Screenshots that will be generated (click to add):</strong>
            <ScreenshotList>
              {getScreenshotCommands().length > 0 ? (
                <>
                  <ScreenshotItem
                    onClick={insertAllScreenshotsPlaceholder}
                    title="Click to insert all individual screenshot placeholders"
                    style={{
                      fontWeight: "bold",
                      borderTop: "1px solid #d9d9d9",
                      marginTop: "4px",
                      paddingTop: "8px"
                    }}>
                    <Icon path={mdiImageOutline} size={0.6} />
                    All Screenshots
                  </ScreenshotItem>
                  <Divider style={{ margin: 0 }} />
                  {getScreenshotCommands().map((screenshot, index) => (
                    <ScreenshotItem
                      key={index}
                      onClick={() => insertScreenshotPlaceholder(index + 1)}
                      title={`Click to insert {{screenshot_${index + 1}}} placeholder`}>
                      <Icon path={mdiImageOutline} size={0.6} />({screenshot.type})
                    </ScreenshotItem>
                  ))}
                </>
              ) : (
                <ScreenshotItem>
                  <Icon path={mdiImageOutline} size={0.6} />
                  No screenshots will be generated
                </ScreenshotItem>
              )}
            </ScreenshotList>
          </ScreenshotPreview>

          <EmailContent>
            <div style={{ marginBottom: 12 }}>
              <label>Email Subject:</label>
              <Input
                value={emailTemplate.subject}
                onChange={(e) =>
                  setEmailTemplate({ ...emailTemplate, subject: e.target.value })
                }
                placeholder="Email subject (supports {{automation_name}} placeholder)"
                style={{ marginTop: 4 }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label>Email Body (Markdown):</label>
              <div style={{ marginTop: 4 }}>
                <MarkdownToolbar>
                  {markdownButtons.map((button, index) => (
                    <MarkdownButton
                      key={index}
                      type="text"
                      size="small"
                      title={button.tooltip}
                      onClick={button.action}
                      icon={<Icon path={button.icon} size={0.7} />}
                    />
                  ))}
                </MarkdownToolbar>
                <MarkdownTextArea
                  id="email-body-textarea"
                  value={emailTemplate.body}
                  onChange={(e) =>
                    setEmailTemplate({ ...emailTemplate, body: e.target.value })
                  }
                  placeholder="Email body in markdown format"
                  rows={9}
                  style={{ fontFamily: "monospace" }}
                />
              </div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                Available placeholders: {"{{automation_name}}, {{date}}"}
              </div>
            </div>
          </EmailContent>
        </EmailTemplateContent>
      )
    },
    {
      key: "preview",
      label: "Preview",
      children: (
        <EmailPreview style={{ padding: 16 }}>
          <strong>Subject:</strong>{" "}
          {emailTemplate.subject.replace(
            /\{\{automation_name\}\}/g,
            automation?.name || "Automation"
          )}
          <br />
          <br />
          <strong>Body:</strong>
          <div
            style={{ marginTop: 8 }}
            dangerouslySetInnerHTML={{
              __html: processScreenshotPlaceholders(
                marked(renderEmailPreview(), { breaks: true, sanitize: true })
              )
            }}
          />
        </EmailPreview>
      )
    }
  ];
  const renderFrequencySettings = (schedule: ScheduleRule, index: number) => {
    switch (schedule.schedule.type) {
      case "weekly":
        return (
          <div>
            <label>Day of Week:</label>
            <DaysOfWeekSelector>
              {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                <DayButton
                  key={day}
                  size="small"
                  selected={schedule.schedule.dayOfWeek === day}
                  onClick={() => toggleDayOfWeek(index, day)}>
                  {getDayLabel(day)}
                </DayButton>
              ))}
            </DaysOfWeekSelector>
          </div>
        );

      case "monthly":
        return (
          <div>
            <label>Day of Month:</label>
            <Select
              style={{ width: 120, marginLeft: 8 }}
              value={schedule.schedule.dayOfMonth}
              onChange={(value) =>
                updateScheduleLocal(index, {
                  schedule: { ...schedule.schedule, dayOfMonth: value }
                })
              }>
              {Array.from({ length: 31 }, (_, i) => (
                <Option key={i + 1} value={i + 1}>
                  {i + 1}
                </Option>
              ))}
            </Select>
          </div>
        );

      default:
        return null;
    }
  };
  const mainTabItems = [
    {
      key: "schedule",
      label: "Schedule",
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              onClick={addNewSchedule}
              disabled={schedules.length > 0}>
              {schedules.length > 0 ? "Schedule Created" : "Add Schedule"}
            </Button>
          </div>
          {schedules.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#666",
                backgroundColor: "#fafafa",
                borderRadius: "8px",
                border: "1px dashed #d9d9d9"
              }}>
              <p style={{ marginBottom: 8 }}>No schedule created yet</p>
              <p style={{ fontSize: 12, margin: 0 }}>
                Click &quot;Add Schedule&quot; to create a schedule
              </p>
            </div>
          ) : (
            schedules.map((schedule, index) => (
              <ScheduleItem key={schedule._id || schedule.id || index}>
                <ScheduleHeader>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4
                        }}>
                        <span style={{ fontWeight: 500, color: "#666" }}>Schedule</span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#fff",
                            backgroundColor:
                              schedule.schedule.type === "once" ? "#1890ff" : "#52c41a",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            textTransform: "capitalize"
                          }}>
                          {schedule.schedule.type}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#999" }}>
                        Next execution: {getNextExecutionTime(schedule)}
                      </div>
                    </div>
                  </div>
                  <IconButton
                    type="text"
                    color="red"
                    icon={<Icon path={mdiDelete} size={1.2} />}
                    onClick={() => removeSchedule(index)}
                  />
                </ScheduleHeader>

                <FrequencyOptions>
                  <label>Frequency:</label>
                  <Radio.Group
                    value={schedule.schedule.type}
                    onChange={(e) =>
                      updateScheduleLocal(index, {
                        schedule: { ...schedule.schedule, type: e.target.value }
                      })
                    }
                    style={{ marginLeft: 12 }}>
                    <Radio value="once">Once</Radio>
                    <Radio value="daily">Daily</Radio>
                    <Radio value="weekly">Weekly</Radio>
                    <Radio value="monthly">Monthly</Radio>
                  </Radio.Group>
                </FrequencyOptions>

                <FormRow>
                  <div>
                    <label>Date:</label>
                    <DatePicker
                      value={schedule.datetime}
                      onChange={(date) => {
                        if (date) {
                          const newDateTime = schedule.datetime
                            ? schedule.datetime
                                .clone()
                                .year(date.year())
                                .month(date.month())
                                .date(date.date())
                            : date;
                          updateScheduleLocal(index, { datetime: newDateTime });
                        }
                      }}
                      style={{ width: "100%", marginLeft: 8 }}
                      disabledDate={(current) =>
                        current && current < moment().startOf("day")
                      }
                    />
                  </div>
                  <div>
                    <label>Time:</label>
                    <TimePicker
                      value={schedule.datetime}
                      onChange={(time) => {
                        if (time) {
                          const newDateTime = schedule.datetime
                            ? schedule.datetime
                                .clone()
                                .hour(time.hour())
                                .minute(time.minute())
                                .second(0)
                            : time;
                          updateScheduleLocal(index, { datetime: newDateTime });
                        }
                      }}
                      style={{ width: "100%", marginLeft: 8 }}
                      format="HH:mm"
                    />
                  </div>
                </FormRow>

                {renderFrequencySettings(schedule, index)}
              </ScheduleItem>
            ))
          )}
        </div>
      )
    },
    {
      key: "email",
      label: "Email Template",
      children: (
        <EmailTemplateSection>
          <CompactTabs defaultActiveKey="edit" items={emailTabItems} />
        </EmailTemplateSection>
      )
    }
  ];

  if (!automation) return null;

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon path={mdiCalendarClock} size={1} />
          Schedule Automation: {automation.name}
        </div>
      }
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      width={800}
      bodyStyle={{ padding: 5 }}
      okText="Save Schedule"
      cancelText="Cancel"
      confirmLoading={isCreating || isUpdating}
      maskClosable={false}>
      <ModalContent>
        <CompactTabs
          defaultActiveKey="schedule"
          items={mainTabItems}
          size="large"
          style={{
            height: "500px",
            overflow: "auto"
          }}
        />
      </ModalContent>
    </Modal>
  );
};
